import { ChevronDown, ChevronUp, X } from "@untitledui/icons";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button as AriaButton } from "react-aria-components";
import { useMerchantRisksList } from "@/api/merchant-risks";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { useFilters } from "@/providers/filter-provider";
import {
  type FilterField,
  type FilterOperator,
  formatRangeValue,
  getActivePills,
  pillsConfig,
  type RangeFilterPairConfig,
} from "@/providers/filter-registry";
import { cx } from "@/utils/cx";

interface FilterPillsProps {
  className?: string;
  /** Pills the page owns rather than the filter form, rendered ahead of them and cleared by their own handler. */
  extraPills?: ReactNode;
}

// Maximum characters to show in a pill value before truncating
const MAX_VALUE_LENGTH = 25;
// Maximum number of items to show in tooltip
const MAX_TOOLTIP_ITEMS = 10;
// Approximate height of a single row of pills (pill height + gap)
const SINGLE_ROW_HEIGHT = 32;
// Maximum rows before switching to accordion mode
const MAX_ROWS = 2;
// Height threshold for accordion mode (2 rows + some buffer)
const MAX_HEIGHT_BEFORE_ACCORDION = SINGLE_ROW_HEIGHT * MAX_ROWS + 8;

interface ActiveFilter {
  id: string; // Can be a single field or combined "minField|maxField" for ranges
  label: string;
  displayValue: string;
  fullValue: string;
  count?: number;
  fieldsToRemove: FilterField[]; // Fields to clear when removing this filter
  canNegate: boolean;
}

const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1)}...`;
};

/**
 * Format range display value based on which values are present.
 * - Both min and max: "£0 - £33K"
 * - Only min: "≥ £0"
 * - Only max: "≤ £33K"
 */
const formatRangeDisplay = (
  minValue: string | undefined,
  maxValue: string | undefined,
  config: RangeFilterPairConfig,
): string => {
  const hasMin = minValue !== undefined && minValue !== "";
  const hasMax = maxValue !== undefined && maxValue !== "";

  if (hasMin && hasMax) {
    const formattedMin = formatRangeValue(minValue, config.format);
    const formattedMax = formatRangeValue(maxValue, config.format);
    if (formattedMin === formattedMax) return formattedMin;
    return `${formattedMin} - ${formattedMax}`;
  }
  if (hasMin) {
    return `≥ ${formatRangeValue(minValue, config.format)}`;
  }
  if (hasMax) {
    return `≤ ${formatRangeValue(maxValue, config.format)}`;
  }
  return "";
};

export const FilterPills = ({ className, extraPills }: FilterPillsProps) => {
  const { form, activeFields, clearField, reset, setOperator } = useFilters();
  const values = form.watch();

  // riskId pills store the raw slug, not a {id,label} pair — look its readable name up from
  // the same merchant risk catalog RiskTypeFilterInner uses, falling back to the raw id for a
  // disabled risk or a stale URL, same as that component's own fallback badges.
  const { data: merchantRisks } = useMerchantRisksList();
  const riskNameById = useMemo(
    () => new Map(merchantRisks?.map((r) => [r.riskId, r.risk.readableName]) ?? []),
    [merchantRisks],
  );

  // State for accordion mode
  const [needsAccordion, setNeedsAccordion] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Refs for measuring content height
  const containerRef = useRef<HTMLDivElement>(null);
  const pillsContainerRef = useRef<HTMLDivElement>(null);

  const filters = useMemo(
    () =>
      getActivePills(values, activeFields).map((pill): ActiveFilter => {
        if (pill.kind === "range") {
          const displayValue = formatRangeDisplay(pill.minValue, pill.maxValue, pill.pair);
          return {
            id: pill.id,
            label: pill.pair.label,
            displayValue,
            fullValue: displayValue,
            fieldsToRemove: pill.fields,
            canNegate: !!pill.notKey,
          };
        }

        const value = values[pill.field];
        const config = pillsConfig[pill.field];
        // riskId can't carry a formatter in pillsConfig itself — it's a static object with no
        // access to the fetched risk catalog.
        const formatter =
          pill.field === "riskId"
            ? (item: unknown) => riskNameById.get(item as string) ?? String(item)
            : config.formatter;
        const hoverLabelFormatter = config.hoverLabel;

        if (Array.isArray(value)) {
          const count = value.length;
          const formattedItems = value.map((item: unknown) => (formatter ? formatter(item) : String(item)));
          const displayValue =
            count === 1
              ? truncateText(formattedItems[0], MAX_VALUE_LENGTH)
              : `${truncateText(formattedItems[0], MAX_VALUE_LENGTH - 5)} +${count - 1}`;
          const fullValue = hoverLabelFormatter
            ? value.map((item: unknown) => hoverLabelFormatter(item)).join(", ")
            : formattedItems.join(", ");
          return {
            id: pill.id,
            label: config.label,
            displayValue,
            fullValue,
            count,
            fieldsToRemove: pill.fields,
            canNegate: !!pill.notKey,
          };
        }

        const formattedValue = formatter ? formatter(value) : String(value);
        return {
          id: pill.id,
          label: config.label,
          displayValue: truncateText(formattedValue, MAX_VALUE_LENGTH),
          fullValue: hoverLabelFormatter ? hoverLabelFormatter(value) : formattedValue,
          fieldsToRemove: pill.fields,
          canNegate: !!pill.notKey,
        };
      }),
    [activeFields, values, riskNameById],
  );

  // Check if content exceeds 2 rows and needs accordion mode
  const checkOverflow = useCallback(() => {
    if (!pillsContainerRef.current) return;

    // Temporarily remove height constraints to measure natural height
    const container = pillsContainerRef.current;
    const originalMaxHeight = container.style.maxHeight;
    container.style.maxHeight = "none";

    const naturalHeight = container.scrollHeight;

    // Restore the constraint
    container.style.maxHeight = originalMaxHeight;

    setNeedsAccordion(naturalHeight > MAX_HEIGHT_BEFORE_ACCORDION);
  }, []);

  // Use ResizeObserver to detect when container size changes
  useEffect(() => {
    const container = pillsContainerRef.current;
    if (!container) return;

    // Initial check
    checkOverflow();

    const resizeObserver = new ResizeObserver(() => {
      checkOverflow();
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [checkOverflow]);

  // Reset expanded state when filters change significantly
  useEffect(() => {
    if (!needsAccordion) {
      setIsExpanded(false);
    }
  }, [needsAccordion]);

  const negatedIds = useMemo(() => new Set(values.negated ?? []), [values.negated]);

  const hasFilters = filters.length > 0 || !!extraPills;

  // Build tooltip content for filters with multiple items
  const getTooltipContent = (filter: ActiveFilter): string => {
    if (!filter.count || filter.count <= 1) {
      // Only show tooltip if the value was truncated
      return filter.fullValue.length > MAX_VALUE_LENGTH ? filter.fullValue : "";
    }

    // For multiple items, show a formatted list
    const items = filter.fullValue.split(", ");
    if (items.length <= MAX_TOOLTIP_ITEMS) {
      return items.join("\n");
    }

    // Truncate if too many items
    const displayedItems = items.slice(0, MAX_TOOLTIP_ITEMS);
    return `${displayedItems.join("\n")}\n... and ${items.length - MAX_TOOLTIP_ITEMS} more`;
  };

  // Handle removing a filter (may clear multiple fields for range pairs)
  const handleRemoveFilter = (filter: ActiveFilter) => {
    for (const field of filter.fieldsToRemove) {
      clearField(field);
    }
  };

  if (!hasFilters) {
    return null;
  }

  // Render the pills grid (used in both normal and accordion modes)
  const renderPills = () => (
    <div
      ref={pillsContainerRef}
      className={cx(
        "flex flex-wrap gap-1.5",
        // In accordion mode: show all when expanded, limit to 2 rows when collapsed
        needsAccordion && !isExpanded && "max-h-[72px] overflow-hidden",
      )}
    >
      {extraPills}
      {filters.map((filter) => {
        const tooltipContent = getTooltipContent(filter);
        const hasTooltip = tooltipContent.length > 0;

        return (
          <Tooltip key={filter.id} isDisabled={!hasTooltip} title={tooltipContent} placement="top">
            <TooltipTrigger>
              <FilterPill
                label={filter.label}
                value={filter.displayValue}
                onRemove={() => handleRemoveFilter(filter)}
                operator={negatedIds.has(filter.id) ? "is_not" : "is"}
                onOperatorChange={filter.canNegate ? (operator) => setOperator(filter.id, operator) : undefined}
              />
            </TooltipTrigger>
          </Tooltip>
        );
      })}
    </div>
  );

  return (
    <div ref={containerRef} className={cx("py-2", className)}>
      <div className="flex items-start gap-2">
        {/* Filters label */}
        <span className="text-xs text-tertiary font-medium whitespace-nowrap flex-shrink-0 pt-1.5">Filters:</span>

        {/* Pills container */}
        <div className="flex-1 min-w-0">{renderPills()}</div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 flex-shrink-0 pt-1">
          {/* Accordion toggle button - only show when in accordion mode */}
          {needsAccordion && (
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className={cx(
                "inline-flex items-center gap-1 text-xs font-medium",
                "text-tertiary hover:text-primary",
                "transition-colors",
              )}
              aria-expanded={isExpanded}
              aria-label={isExpanded ? "Collapse filters" : "Expand filters"}
            >
              {isExpanded ? (
                <>
                  <span>Less</span>
                  <ChevronUp className="w-3.5 h-3.5" />
                </>
              ) : (
                <>
                  <span>More</span>
                  <ChevronDown className="w-3.5 h-3.5" />
                </>
              )}
            </button>
          )}

          {/* Clear all button */}
          {filters.length > 1 && (
            <button
              type="button"
              onClick={reset}
              className="text-xs text-tertiary hover:text-primary font-medium whitespace-nowrap transition-colors"
            >
              Clear all
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const OPERATOR_LABELS: Record<FilterOperator, string> = {
  is: "is",
  is_not: "is not",
};

const OPERATORS: FilterOperator[] = ["is", "is_not"];

export interface FilterPillProps {
  label: string;
  value: string;
  onRemove: () => void;
  operator?: FilterOperator;
  onOperatorChange?: (operator: FilterOperator) => void;
}

const PillDivider = () => <span aria-hidden="true" className="h-full w-px shrink-0 bg-border-primary" />;

export const FilterPill = ({ label, value, onRemove, operator = "is", onOperatorChange }: FilterPillProps) => {
  return (
    <div
      className={cx(
        "inline-flex h-[30px] max-w-[360px] items-center overflow-hidden",
        "rounded-md border border-primary bg-secondary",
        "text-xs font-medium leading-[18px]",
      )}
    >
      <span className="flex h-full items-center whitespace-nowrap px-2 text-primary">{label}</span>
      {onOperatorChange && (
        <>
          <PillDivider />
          <Dropdown.Root>
            <AriaButton
              aria-label={`${label} operator`}
              className={cx(
                "flex h-full cursor-pointer items-center whitespace-nowrap px-2 text-tertiary",
                "transition-colors hover:bg-primary_hover",
                "outline-focus-ring focus-visible:outline-2 focus-visible:-outline-offset-2",
              )}
            >
              {OPERATOR_LABELS[operator]}
            </AriaButton>
            <Dropdown.Popover placement="bottom start" className="w-28">
              <Dropdown.Menu
                selectedKeys={[operator]}
                onSelectionChange={(keys) => {
                  const next = [...keys][0] as FilterOperator | undefined;
                  if (next) onOperatorChange(next);
                }}
              >
                {OPERATORS.map((option) => (
                  <Dropdown.Item
                    key={option}
                    id={option}
                    textValue={OPERATOR_LABELS[option]}
                    className={(state) => cx("[&_span]:text-xs", state.isSelected && "[&>div]:bg-active")}
                  >
                    {OPERATOR_LABELS[option]}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown.Popover>
          </Dropdown.Root>
        </>
      )}
      <PillDivider />
      <span className="flex h-full min-w-0 items-center pl-2 pr-1 text-primary" title={value}>
        <span className="truncate">{value}</span>
      </span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className={cx(
          "flex h-full shrink-0 items-center pl-1 pr-2",
          "text-fg-quaternary transition-colors hover:text-fg-quaternary_hover",
          "outline-focus-ring focus-visible:outline-2 focus-visible:-outline-offset-2",
        )}
        aria-label={`Remove ${label} filter`}
      >
        <X className="size-3" />
      </button>
    </div>
  );
};
